import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckSquare, CheckCircle2, CircleOff, Download, Trash2 } from 'lucide-react';
import { PlusIcon, ChevronLeftIcon } from '@/components/icons';
import { Loader, Tab, ActionDropdown, DeleteModal, PublishModal, Pagination, Checkbox, type TabItem, type DropdownAction, Button } from '@/components/ui';
import { useFilterGalleries, useDeleteGallery, useUpdateGalleryStatus, usePublishedGalleryStats, useDraftGalleryStats, useSearchGalleries } from '@/lib/api/hooks/gallery';
import { showToast } from '@/lib/toast';
import ImageViewerModal from '@/pages/multimedia/photo-gallery/modals/ImageViewerModal';
import { handleDownloadFromUrl as downloadFileFromUrl } from '@/utils/helpers/handleDownloadFromUrl';
import { Paragraph } from '@/components/ui/Typography';
import Search from '@/components/ui/Search';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { usePhotoGalleryListFilterSections } from '@/pages/multimedia/photo-gallery/FilterModule/FILTER_SECTIONS';

type GalleryCardItem = {
    photoGalleryId: string;
    title: string;
    dateCreated?: string;
    status: string;
    photoUrl?: string | null;
    publishedByName?: string;
};

// type GalleryFolderItem = {
//     folderId: string;
//     folderName: string;
//     titleResponseList: GalleryCardItem[];
// };

const PhotoGalleryList: React.FC = () => {
    const { folderId } = useParams<{ folderId: string }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');

    // Image viewer state
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [currentGalleryPhotos, setCurrentGalleryPhotos] = useState<string[]>([]);

    // Modal states
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [selectedGalleryForDelete, setSelectedGalleryForDelete] = useState<{ id: string; title: string } | null>(null);
    const [selectedGalleryForStatusUpdate, setSelectedGalleryForStatusUpdate] = useState<{ id: string; title: string; status: 'PUBLISH' | 'DRAFT' } | null>(null);
    const [selectedGalleryIds, setSelectedGalleryIds] = useState<string[]>([]);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

 const FILTER_SECTIONS = usePhotoGalleryListFilterSections();
              // ── URL-driven search / filter state ──────────────────────────────────────
              const {
                mode,
                searchTerm,
                filterState,
                page,
                size,
                setSearch,
                setFilters,
                removeFilter,
                clearFilters,
                setPage,
                apiFilters,
                getActiveFilterCount
              } = useSearchFilterParams(FILTER_SECTIONS)
            
            // Search API (when searching)
            const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchGalleries(
              { page, size },
            searchTerm,
              mode === 'search' 
            )
          
            // Filter API (when filtering or default)
            const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFilterGalleries(
              { page, size },
              {   status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT', folderId, ...apiFilters },
              mode === 'filter' || mode === 'default'
            )
          
            // Select appropriate data source: search takes priority
            const aggregateData = mode === 'search' ? searchData : filterData
            const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
            const isError = mode === 'search' ? isSearchError : isFilterError
          
            const totalPages = aggregateData?.data?.totalPage ?? 1
            const activeFilterCount = getActiveFilterCount()
            const refetchData = mode === 'search' ? refetchSearch : refetchFilter
            const emptyMessage = mode === 'search'
              ? 'No gallery found matching your search criteria.'
              : mode === "filter" ?  'No gallery found matching your filter criteria.'
              : 'No gallery found.'
    

    // Fetch galleries for this folder with pagination
    // const { data: galleriesData, isLoading: galleriesLoading } = useFilterGalleries(
    //     { page: currentPage, size: pageSize },
    //     { folderId }
    // );
    const { data: publishedStatsData } = usePublishedGalleryStats();
    const { data: draftStatsData } = useDraftGalleryStats();

    const { mutate: deleteGallery, isPending: isDeleting } = useDeleteGallery();
    const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateGalleryStatus();

    // Get folder data and galleries
    const folders = useMemo(() => aggregateData?.data?.data || [], [aggregateData?.data?.data]);

    const folderData = useMemo(() => folders.find((f: { folderId: string | undefined; }) => f.folderId === folderId), [folders, folderId]);
    const allGalleries = useMemo(() => folderData?.titleResponseList ?? [], [folderData]);

    const selectedCount = selectedGalleryIds.length;

    // Filter galleries based on tab, search term
    const filteredGalleries: { status: string; title: string; publishedByName: string; photoGalleryId: string; photoUrl?: string; dateCreated?: string }[] = useMemo(() => {
        return allGalleries.filter((gallery: { status: string; title: string; publishedByName: string; photoGalleryId: string; photoUrl?: string; dateCreated?: string }) => {
            // Filter by status
            const statusMatch = activeTab === 'published'
                ? gallery.status === 'PUBLISH'
                : gallery.status === 'DRAFT';

            if (!statusMatch) return false;

            // Filter by search term
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                return (
                    gallery.title?.toLowerCase().includes(searchLower) ||
                    gallery.publishedByName?.toLowerCase().includes(searchLower)
                );
            }

            return true;
        });
    }, [allGalleries, activeTab, searchTerm]);

    useEffect(() => {
        const visibleGalleryIds = new Set(filteredGalleries.map((gallery: { photoGalleryId: string }) => gallery.photoGalleryId));
        setSelectedGalleryIds((prev) => prev.filter((id) => visibleGalleryIds.has(id)));
    }, [filteredGalleries]);

    useEffect(() => {
        setSelectedGalleryIds([]);
        setIsBulkProcessing(false);
    }, [activeTab]);

    const handleDeleteGallery = (galleryId: string, galleryTitle: string) => {
        setSelectedGalleryForDelete({ id: galleryId, title: galleryTitle });
        setDeleteModalOpen(true);
    };

    const confirmDeleteGallery = () => {
        if (!selectedGalleryForDelete) return;

        deleteGallery(selectedGalleryForDelete.id, {
            onSuccess: (response) => {
                if (response.success) {
                    showToast.success('Gallery deleted successfully');
                    setDeleteModalOpen(false);
                    setSelectedGalleryForDelete(null);
                } else {
                    showToast.error('Failed to delete gallery');
                }
            },
            onError: (error: Error) => {
                showToast.error('Failed to delete gallery: ' + (error.message || 'Unknown error'));
            }
        });
    };

    const handleStatusUpdateClick = (gallery: { id: string; title: string; status: string }) => {
        const newStatus = gallery.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';
        setSelectedGalleryForStatusUpdate({
            id: gallery.id,
            title: gallery.title,
            status: newStatus as 'PUBLISH' | 'DRAFT'
        });
        setPublishModalOpen(true);
    };

    const confirmStatusUpdate = () => {
        if (!selectedGalleryForStatusUpdate) return;

        updateStatus(
            {
                galleryId: selectedGalleryForStatusUpdate.id,
                data: { status: selectedGalleryForStatusUpdate.status }
            },
            {
                onSuccess: (response) => {
                    if (response.success) {
                        showToast.success(
                            selectedGalleryForStatusUpdate.status === 'PUBLISH'
                                ? 'Gallery published successfully!'
                                : 'Gallery unpublished successfully!'
                        );
                        setPublishModalOpen(false);
                        setSelectedGalleryForStatusUpdate(null);
                    }
                },
                onError: (error: Error) => {
                    showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
                }
            }
        );
    };

    const getDropdownActions = (gallery: GalleryCardItem): DropdownAction[] => {
        const galleryId = gallery.photoGalleryId;
        const actions: DropdownAction[] = [{
            label: "Edit Image",
            onClick: (e) => {
                e.stopPropagation();
                navigate(`/admin/photo-gallery/${folderId}/edit/${galleryId}`);
            }
        }];

        // Publish/Unpublish option
        actions.push({
            label: gallery.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
            onClick: (e) => {
                e.stopPropagation();
                handleStatusUpdateClick({ id: galleryId, title: gallery.title, status: gallery.status });
            },
            disabled: isUpdatingStatus
        });

        // Delete option (only for drafts)
        if (gallery.status === 'DRAFT') {
            actions.push({
                label: 'Delete',
                onClick: (e) => {
                    e.stopPropagation();
                    handleDeleteGallery(galleryId, gallery.title);
                },
                variant: 'danger' as const,
                disabled: isDeleting
            });
        }

        return actions;
    };

    const openImageViewer = (photoUrl: string) => {
        if (photoUrl) {
            setCurrentGalleryPhotos([photoUrl]);
            setSelectedImageIndex(0);
        }
    };

    const closeImageViewer = () => {
        setSelectedImageIndex(null);
        setCurrentGalleryPhotos([]);
    };

    const tabItems: TabItem<'published' | 'drafts'>[] = [
        { key: 'published', label: 'Published', count: publishedStatsData?.data ?? 0 },
        { key: 'drafts', label: 'Drafts', count: draftStatsData?.data ?? 0 }
    ];

    const toggleGallerySelection = (galleryId: string) => {
        setSelectedGalleryIds((prev) =>
            prev.includes(galleryId) ? prev.filter((id) => id !== galleryId) : [...prev, galleryId]
        );
    };

    const clearSelection = () => {
        setSelectedGalleryIds([]);
    };

    const updateGalleryStatusAsync = (galleryId: string, status: 'PUBLISH' | 'DRAFT') => {
        return new Promise<void>((resolve, reject) => {
            updateStatus(
                { galleryId, data: { status } },
                {
                    onSuccess: (response) => {
                        try {
                            if (response?.success === true) {
                                resolve();
                            } else {
                                reject(new Error('Failed to update one or more galleries'));
                            }
                        } catch {
                            reject(new Error('Failed to update one or more galleries'));
                        }
                    },
                    onError: reject,
                }
            );
        });
    };

    const deleteGalleryAsync = (galleryId: string) => {
        return new Promise<void>((resolve, reject) => {
            deleteGallery(galleryId, {
                onSuccess: (response) => {
                    try {
                        if (response?.success === true) {
                            resolve();
                        } else {
                            reject(new Error('Failed to delete one or more galleries'));
                        }
                    } catch {
                        reject(new Error('Failed to delete one or more galleries'));
                    }
                },
                onError: reject,
            });
        });
    };

    const runBulkStatusUpdate = async (status: 'PUBLISH' | 'DRAFT') => {
        if (!selectedGalleryIds.length || isBulkProcessing) return;
        setIsBulkProcessing(true);
        try {
            const results = await Promise.allSettled(
                selectedGalleryIds.map((galleryId) => updateGalleryStatusAsync(galleryId, status))
            );

            const successfulCount = results.filter((result) => result.status === 'fulfilled').length;
            const failedCount = results.length - successfulCount;

            if (successfulCount > 0) {
                showToast.success(
                    status === 'PUBLISH'
                        ? `${successfulCount} selected galleries published`
                        : `${successfulCount} selected galleries unpublished`
                );
                clearSelection();
            }

            if (failedCount > 0) {
                showToast.error(`Failed to update ${failedCount} ${failedCount === 1 ? 'gallery' : 'galleries'}`);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk status update failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const runBulkDelete = async () => {
        if (!selectedGalleryIds.length || isBulkProcessing) return;
        setIsBulkProcessing(true);
        try {
            const results = await Promise.allSettled(selectedGalleryIds.map((galleryId) => deleteGalleryAsync(galleryId)));
            const successfulCount = results.filter((result) => result.status === 'fulfilled').length;
            const failedCount = results.length - successfulCount;

            if (successfulCount > 0) {
                showToast.success(`${successfulCount} selected galleries deleted`);
                clearSelection();
            }

            if (failedCount > 0) {
                showToast.error(`Failed to delete ${failedCount} ${failedCount === 1 ? 'gallery' : 'galleries'}`);
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk delete failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const runBulkDownload = async () => {
        if (!selectedGalleryIds.length || isBulkProcessing) return;

        const selectedGalleries = filteredGalleries.filter((gallery) => selectedGalleryIds.includes(gallery.photoGalleryId));
        const downloadableGalleries = selectedGalleries.filter((gallery) => Boolean(gallery.photoUrl));

        if (!downloadableGalleries.length) {
            showToast.error('No downloadable files found for selected galleries');
            return;
        }

        setIsBulkProcessing(true);
        try {
            await Promise.all(
                downloadableGalleries.map((gallery) =>
                    downloadFileFromUrl({
                        url: gallery.photoUrl as string,
                        fileName: gallery.title || 'gallery-image',
                    })
                )
            );
            showToast.success(`${downloadableGalleries.length} selected gallery files downloaded`);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Bulk download failed';
            showToast.error(message);
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const getStatusPillClass = (status: string) => {
        if (status === 'PUBLISH') return 'bg-[#2F855A] text-white';
        if (status === 'DRAFT') return 'bg-[#ED8936] text-white';
        return 'bg-gray-700 text-white';
    };

    const getFormattedDateTime = (dateValue?: string) => {
        if (!dateValue) return { date: 'N/A', time: 'N/A' };
        const parsed = new Date(dateValue);
        if (Number.isNaN(parsed.getTime())) return { date: 'N/A', time: 'N/A' };

        return {
            date: parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            time: parsed.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        };
    };

        if (!folderId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Folder ID missing</h2>
                <button onClick={() => navigate('/admin/photo-gallery')} className="mt-4 text-[#047857] hover:underline">
                    ← Back to Photo Gallery
                </button>
            </div>
        );
    }

  const isEmptyState = !isFetching && mode === "default" && filteredGalleries.length === 0;

    return (
        <>
            <div className="space-y-6">
                {/* Breadcrumb and Tabs */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            onClick={() => navigate('/admin/photo-gallery')}
                            className="text-[#047857] hover:underline font-medium"
                        >
                            Photo Gallery
                        </button>
                        <ChevronLeftIcon className="w-4 h-4 rotate-180 text-gray-400" />
                        <span className="text-gray-700 font-medium">{folderData?.folderName || '-'}</span>
                    </div>
                    <Tab
                        tabs={tabItems}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                    />
                </div>

                {/* Search and Filter row */}
                <div className="flex items-center justify-between gap-4">
                    <div className="relative w-80">
                        <Search
                                                    value={searchTerm}
                                                    onSearch={setSearch}
                                                    placeholder="Search"
                                                  />
                    </div>
                    <div className="flex items-center gap-4">
                           <FilterModule
                                    sections={FILTER_SECTIONS}
                                    activeFilters={filterState}
                                    onApply={setFilters}
                                    activeFilterCount={activeFilterCount}
                                  />
                        <Button
                            onClick={() => navigate(`/admin/photo-gallery/${folderId}/upload`)}
                            className="bg-[#047857] hover:bg-[#059669] text-white font-semibold"
                        >
                            Upload
                        </Button>
                    </div>
                </div>

                 {/* Active filter chips — only shown in filter mode */}
                                            <FilterParamsDisplay
                                              sections={FILTER_SECTIONS}
                                              filterState={filterState}
                                              onRemove={removeFilter}
                                              onClearAll={clearFilters}
                                            />

                {/* Content */}
                <div className="p-5">
                    {isFetching ? <Loader fullPage size="lg" variant="wine" message="Loading photos..." /> :
                                            isError ? (
                                        <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
                                          <Paragraph className="">Error loading data.</Paragraph>
                                          <Button
                                            onClick={() => {
                                              refetchData();
                                            }}
                                            className=""
                                          >
                                            Retry
                                          </Button>
                                        </div>
                                      )  : isEmptyState  ? (
                                              <div className="text-center py-20">
                                                <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                                                <img src="/No-post-icon.png" alt="No folders" className="mx-auto h-20 w-20 object-contain" />
                                                </div>
                                              <h3 className="mt-4 text-[2rem] leading-none font-semibold text-gray-800">
                                    {mode !== "default" ? 'No folders found' : 'No Folders Yet'}
                                  </h3>
                                                 <p className="mt-1 text-sm text-gray-500">
                                    {emptyMessage}
                                  </p>
                                                  <Button variant="primary" className="mt-4" onClick={() => navigate(`/admin/photo-gallery/${folderId}/upload`)}> <PlusIcon className="w-4 h-4" />
                                                    Upload Photos
                                                  </Button>
            
                                              </div>
                                            ) : filteredGalleries.length > 0 ? (
                        <>
                            {selectedCount > 0 && (
                                <div className="mb-4 flex   items-center overflow-hidden rounded-xl border border-gray-200 bg-white">
                                    <div className="flex items-center gap-2 border-r border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                                        <CheckSquare className="h-4 w-4 text-[#0F8A47]" />
                                        {selectedCount} selected
                                    </div>

                                    {activeTab === 'drafts' && (
                                        <button
                                            type="button"
                                            onClick={() => runBulkStatusUpdate('PUBLISH')}
                                            disabled={isBulkProcessing || isUpdatingStatus}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-[#0F8A47] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CheckCircle2 className="h-4 w-4" /> Publish
                                        </button>
                                    )}

                                    {activeTab === 'published' && (
                                        <button
                                            type="button"
                                            onClick={() => runBulkStatusUpdate('DRAFT')}
                                            disabled={isBulkProcessing || isUpdatingStatus}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <CircleOff className="h-4 w-4" /> Unpublish
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={runBulkDownload}
                                        disabled={isBulkProcessing}
                                        className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Download className="h-4 w-4" /> Download
                                    </button>

                                    {activeTab === 'drafts' && (
                                        <button
                                            type="button"
                                            onClick={runBulkDelete}
                                            disabled={isBulkProcessing || isDeleting}
                                            className="inline-flex items-center justify-center gap-2 border-r border-gray-200 px-3 py-3 text-sm font-semibold text-[#E53E3E] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <Trash2 className="h-4 w-4" /> Delete
                                        </button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={clearSelection}
                                        disabled={isBulkProcessing}
                                        className="px-4 py-3 text-sm font-semibold text-[#0F8A47] disabled:cursor-not-allowed disabled:opacity-50 ml-auto"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
                                {filteredGalleries.map((gallery) => (
                                    <div
                                        key={gallery.photoGalleryId}
                                        className={`group relative overflow-visible rounded-2xl border bg-white shadow-sm transition ${
                                            selectedGalleryIds.includes(gallery.photoGalleryId)
                                                ? 'border-[#2F855A] ring-1 ring-[#2F855A]/40'
                                                : 'border-gray-200'
                                        }`}
                                    >
                                        <div
                                            className="relative cursor-pointer overflow-hidden rounded-t-2xl"
                                            onClick={() => gallery.photoUrl && openImageViewer(gallery.photoUrl)}
                                        >
                                            <button
                                                className="absolute left-3 top-3 z-10"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedGalleryIds.includes(gallery.photoGalleryId)}
                                                    onChange={() => toggleGallerySelection(gallery.photoGalleryId)}
                                                    ariaLabel={
                                                        selectedGalleryIds.includes(gallery.photoGalleryId)
                                                            ? 'Deselect gallery'
                                                            : 'Select gallery'
                                                    }
                                                />
                                            </button>

                                            {gallery.photoUrl ? (
                                                <img
                                                    src={gallery.photoUrl}
                                                    alt={gallery.title}
                                                    className="h-48 2xl:h-56 w-full object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-48 2xl:h-56 w-full items-center justify-center bg-gray-100">
                                                    <img src="/No-post-icon.png" alt="No photo" className="w-16 h-16 object-contain opacity-50" />
                                                </div>
                                            )}

                                            <div className={`absolute bottom-3 left-3 inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold uppercase tracking-wide ${getStatusPillClass(gallery.status)}`}>
                                                {gallery.status.toLowerCase() === 'publish' ? 'Published' : gallery.status.toLowerCase() === 'draft' ? 'Draft' : 'UNKNOWN'}
                                            </div>

                                        </div>

                                        <div className="flex items-end justify-between gap-3 px-4 py-3">
                                            <div className="min-w-0 flex-1">
                                                <p className="line-clamp-1 leading-tight font-semibold">{gallery.title}</p>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    {(() => {
                                                        const { date, time } = getFormattedDateTime(gallery.dateCreated);
                                                        return `${date}  •  ${time}`;
                                                    })()}
                                                </p>
                                            </div>
                                            <div className="shrink-0 relative z-20">
                                                <ActionDropdown
                                                    id={gallery.photoGalleryId}
                                                    actions={getDropdownActions(gallery)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Pagination */}
                     
                                <Pagination
                               currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
                      className=" p-4"
                                />
                         
                        </>
                    ) : (
                        <div className="text-center py-20">
                            <img src="/No-post-icon.png" alt="No photos" className="w-20 h-20 object-contain mx-auto" />
                            <h3 className="mt-4 font-semibold text-gray-800">
                                {mode !== "default" ? 'No galleries found' : 'No Photos Yet'}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {emptyMessage}
                            </p>
                      
                        </div>
                    )}
                </div>
            </div>

            {/* Image Viewer Modal */}
            <ImageViewerModal
                isOpen={selectedImageIndex !== null}
                onClose={closeImageViewer}
                images={currentGalleryPhotos}
                currentIndex={selectedImageIndex || 0}
                onNext={() => setSelectedImageIndex(i => i !== null && i < currentGalleryPhotos.length - 1 ? i + 1 : i)}
                onPrev={() => setSelectedImageIndex(i => i !== null && i > 0 ? i - 1 : i)}
            />

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDeleteGallery}
                title="Delete Gallery"
                subtitle="Are you sure you want to delete this gallery?"
                itemName={selectedGalleryForDelete?.title}
                warningText="This action cannot be undone. The gallery will be permanently deleted."
                isLoading={isDeleting}
            />

            {/* Publish Modal */}
            {selectedGalleryForStatusUpdate && (
                <PublishModal
                    isOpen={publishModalOpen}
                    onClose={() => setPublishModalOpen(false)}
                    onConfirm={confirmStatusUpdate}
                    title={selectedGalleryForStatusUpdate.status === 'PUBLISH' ? 'Publish Gallery?' : 'Unpublish Gallery?'}
                    subtitle={`Are you sure you want to ${selectedGalleryForStatusUpdate.status === 'PUBLISH' ? 'publish' : 'unpublish'} this gallery?`}
                    itemName={selectedGalleryForStatusUpdate.title}
                    infoText={selectedGalleryForStatusUpdate.status === 'PUBLISH'
                        ? 'This gallery will be visible to the public once published.'
                        : 'This gallery will no longer be visible to the public.'}
                    itemType="Gallery"
                    isLoading={isUpdatingStatus}
                />
            )}
        </>
    );
};

export default PhotoGalleryList;
